import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { useAuth } from '../contexts/AuthContext';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { ApiError } from '../services/api/client';

export default function LoginPage() {
  const { user, loading, login, completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Verificação em dois passos (PRD 39.2): depois da senha, a conta pode pedir
  // um código. O desafio vive só na memória desta tela — fechar a aba o perde.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');

  // Para onde ir depois de entrar. Quem chegou por um QR Code volta para o
  // convite; os demais vao para a home.
  const destination = (location.state as { from?: string } | null)?.from ?? '/';

  // Quem veio de um convite de turma e ainda nao tem conta precisa voltar
  // para a tela do convite — e la que "Criar conta" carrega o token certo
  // (PRD 33.1). Sem isso, o link "Cadastre-se" cairia em /cadastro sem
  // convite algum.
  const cameFromClassInvite = destination.startsWith('/entrar/turma/');

  if (loading) {
    return (
      <div className="centered-state">
        <p>Carregando…</p>
      </div>
    );
  }

  // Ja logado: sai daqui. Precisa respeitar `destination` — se mandar sempre
  // para "/", esta guarda dispara no re-render logo apos o login e engole o
  // redirecionamento para o convite.
  if (user) {
    return <Navigate to={destination} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await login(email, password);

      if (result.twoFactorRequired) {
        setChallenge(result.challenge);
        setPassword('');
        return;
      }

      void navigate(destination, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSecondStep(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;

    setError('');
    setSubmitting(true);

    try {
      await completeTwoFactor(challenge, code);
      void navigate(destination, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente novamente.';
      setError(message);

      // Desafio vencido: não adianta tentar de novo, volta para a senha.
      if (err instanceof ApiError && err.status === 401 && /expirou/i.test(message)) {
        setChallenge(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (challenge) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <p className="eyebrow">Gestão de Processos</p>
          <h1>Verificação em dois passos</h1>
          <p className="muted">
            Digite o código de 6 dígitos do seu aplicativo autenticador. Se perdeu o celular, use um
            código de recuperação.
          </p>

          <form onSubmit={handleSecondStep} noValidate>
            <label htmlFor="code">Código</label>
            <input
              id="code"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="primary" disabled={submitting || code.trim() === ''}>
              {submitting ? 'Verificando…' : 'Verificar'}
            </button>
          </form>

          <p className="auth-switch">
            <button
              type="button"
              className="as-link"
              onClick={() => {
                setChallenge(null);
                setCode('');
                setError('');
              }}
            >
              Voltar
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Gestão de Processos</p>
        <h1>Entrar</h1>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="email">E-mail</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="password">Senha</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        <SocialLoginButtons />

        <p className="auth-switch">
          Ainda não tem conta?{' '}
          {cameFromClassInvite ? (
            // Volta para a tela do convite — lá "Criar conta" já carrega o
            // token certo. /cadastro sozinho não tem de onde tirar o token.
            <Link to={destination}>Cadastre-se como aluno</Link>
          ) : (
            <Link to="/cadastro" state={{ from: destination }}>
              Cadastre-se como aluno
            </Link>
          )}
        </p>
      </div>
    </div>
  );
}
